import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { 
  getPopularVideos, 
  getVideosBySeries, 
  getVideosByCategory,
  getVideoById,
  getVideoBySlug,
  getVideosByLanguage,
  getSimilarVideos,
  getPopularNonSeriesVideos,
  getNonSeriesVideosByCategory,
  getNonSeriesVideosByLanguage
} from '../api/videosAPI';

// Hook for popular videos
export const usePopularVideos = (page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['popularVideos', page, limit],
    () => getPopularVideos(page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch popular videos';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: false,
      staleTime: 300000, // Consider data fresh for 5 minutes
      cacheTime: 3600000, // Keep data in cache for 1 hour
    }
  );

  // Extract videos and pagination from response
  const videos = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    totalPages: 0,
    currentPage: page,
    limit
  };

  return {
    videos,
    pagination,
    isLoading,
    error,
    refetchVideos: refetch
  };
};

// Hook for videos by series
export const useVideosBySeries = (seriesId, page = 1, limit = 10, orderBy = 'created_at', order = 'DESC') => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['videosBySeries', seriesId, page, limit, orderBy, order],
    () => getVideosBySeries(seriesId, page, limit, orderBy, order),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch series videos';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      keepPreviousData: true,
      enabled: seriesId !== undefined && seriesId !== null,
    }
  );

  // Extract videos and pagination from response
  const videos = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    totalPages: 0,
    currentPage: page,
    limit
  };

  return {
    videos,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchVideos: refetch
  };
};

// Hook for videos by category
export const useVideosByCategory = (categoryId, page = 1, limit = 10, orderBy = 'created_at', order = 'DESC') => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['videosByCategory', categoryId, page, limit, orderBy, order],
    () => getVideosByCategory(categoryId, page, limit, orderBy, order),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch category videos';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      keepPreviousData: true,
      enabled: categoryId !== undefined && categoryId !== null,
    }
  );

  // Extract videos and pagination from response
  const videos = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    totalPages: 0,
    currentPage: page,
    limit
  };

  return {
    videos,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchVideos: refetch
  };
};

// Hook for fetching a single video by ID
export const useVideo = (videoId) => {
  const { setError } = useAuthStore();

  const {
    data: video,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['video', videoId],
    () => getVideoById(videoId),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch video details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: videoId !== undefined && videoId !== null,
    }
  );

  return {
    video,
    isLoading,
    error,
    refetchVideo: refetch
  };
};

// Hook for fetching a single video by slug
export const useVideoBySlug = (slug) => {
  const { setError } = useAuthStore();

  const {
    data: video,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['videoBySlug', slug],
    () => getVideoBySlug(slug),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch video details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: slug !== undefined && slug !== null && slug !== '',
    }
  );

  return {
    video,
    isLoading,
    error,
    refetchVideo: refetch
  };
};

// Hook for videos by language
export const useVideosByLanguage = (language, page = 1, limit = 10, orderBy = 'created_at', order = 'DESC') => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['videosByLanguage', language, page, limit, orderBy, order],
    () => getVideosByLanguage(language, page, limit, orderBy, order),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch language videos';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      keepPreviousData: true,
      enabled: language !== undefined && language !== null && language !== '',
    }
  );

  // Extract videos and pagination from response
  const videos = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    totalPages: 0,
    currentPage: page,
    limit
  };

  return {
    videos,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchVideos: refetch
  };
};

// Hook for similar videos
export const useSimilarVideos = (videoId, limit = 6) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['similarVideos', videoId, limit],
    () => getSimilarVideos(videoId, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch similar videos';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      enabled: videoId !== undefined && videoId !== null,
    }
  );

  // Extract videos from response
  const videos = data?.data || [];

  return {
    videos,
    isLoading,
    error,
    refetchVideos: refetch
  };
};

// Hook for popular non-series videos
export const usePopularNonSeriesVideos = (page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['popularNonSeriesVideos', page, limit],
    () => getPopularNonSeriesVideos(page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch popular non-series videos';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: false,
      staleTime: 300000,
      cacheTime: 3600000,
    }
  );

  // Extract videos and pagination from response
  const videos = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    totalPages: 0,
    currentPage: page,
    limit
  };

  return {
    videos,
    pagination,
    isLoading,
    error,
    refetchVideos: refetch
  };
};

// Hook for non-series videos by category
export const useNonSeriesVideosByCategory = (categoryId, page = 1, limit = 10, orderBy = 'created_at', order = 'DESC') => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['nonSeriesVideosByCategory', categoryId, page, limit, orderBy, order],
    () => getNonSeriesVideosByCategory(categoryId, page, limit, orderBy, order),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch non-series category videos';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      keepPreviousData: true,
      enabled: categoryId !== undefined && categoryId !== null,
    }
  );

  // Extract videos and pagination from response
  const videos = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    totalPages: 0,
    currentPage: page,
    limit
  };

  return {
    videos,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchVideos: refetch
  };
};

// Hook for non-series videos by language
export const useNonSeriesVideosByLanguage = (language, page = 1, limit = 10, orderBy = 'created_at', order = 'DESC') => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['nonSeriesVideosByLanguage', language, page, limit, orderBy, order],
    () => getNonSeriesVideosByLanguage(language, page, limit, orderBy, order),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch non-series language videos';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      keepPreviousData: true,
      enabled: language !== undefined && language !== null && language !== '',
    }
  );

  // Extract videos and pagination from response
  const videos = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    totalPages: 0,
    currentPage: page,
    limit
  };

  return {
    videos,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchVideos: refetch
  };
};

// Create a named export object to fix the ESLint warning
const videoHooks = {
  usePopularVideos,
  useVideosBySeries,
  useVideosByCategory,
  useVideo,
  useVideoBySlug,
  useVideosByLanguage,
  useSimilarVideos,
  usePopularNonSeriesVideos,
  useNonSeriesVideosByCategory,
  useNonSeriesVideosByLanguage
};

// Export the named object as default
export default videoHooks;